/**
 * Route-scoped canvas id (`:canvasId` param or explicit `canvasId` for `canvas-capture.html`).
 * Sits above `CanvasLoroProvider` so id is available without coupling to Loro document lifecycle.
 */

import { createContext, useContext, type ReactNode } from "react";
import { useParams } from "react-router";

export interface CanvasRouteContextValue {
  canvasId: string | undefined;
}

const CanvasRouteContext = createContext<CanvasRouteContextValue | null>(null);

export { CanvasRouteContext };

export function useCanvasRoute(): CanvasRouteContextValue {
  const ctx = useContext(CanvasRouteContext);
  if (!ctx) {
    throw new Error("useCanvasRoute must be used within CanvasRouteProvider");
  }
  return ctx;
}

export function CanvasRouteProvider({
  children,
  canvasId: canvasIdFromProps,
}: {
  children: ReactNode;
  /** When set (e.g. `canvas-capture.html?canvasId=`), overrides `:canvasId` from the router. */
  canvasId?: string;
}) {
  const { canvasId: canvasIdFromParams } = useParams<{ canvasId: string }>();
  const canvasId = canvasIdFromProps ?? canvasIdFromParams;
  return (
    <CanvasRouteContext.Provider value={{ canvasId }}>
      {children}
    </CanvasRouteContext.Provider>
  );
}
